import React, { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import styles from './PaymentMethodModal.module.css';
import { homeownerBillingService } from '../../../services/api';

const StripePaymentMethodModal = ({ isOpen, onClose, onSave, editingMethod = null }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [clientSecret, setClientSecret] = useState('');
    const [cardHolderName, setCardHolderName] = useState('');
    const [isDefault, setIsDefault] = useState(false);

    // Card element options
    const cardElementOptions = {
        style: {
            base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                    color: '#aab7c4',
                },
            },
            invalid: {
                color: '#9e2146',
            },
        },
        hidePostalCode: false,
    };

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen && !editingMethod) {
            setCardHolderName('');
            setIsDefault(false);
            setErrors({});
            setIsSubmitting(false);
            setClientSecret(null); // Reset any existing client secret
            createSetupIntent();
        } else if (isOpen && editingMethod) {
            setCardHolderName(editingMethod.card_holder_name || '');
            setIsDefault(editingMethod.is_default || false);
            setErrors({});
            setIsSubmitting(false);
        } else if (!isOpen) {
            // Clear client secret when modal closes to prevent reuse
            setClientSecret(null);
        }
    }, [isOpen, editingMethod]);

    const createSetupIntent = async () => {
        try {
            const response = await homeownerBillingService.createSetupIntent();
            setClientSecret(response.data.data.client_secret);
        } catch (error) {
            console.error('Error creating setup intent:', error);
            setErrors({ setup: 'Failed to initialize payment setup' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!stripe || !elements) {
            return;
        }

        if (editingMethod) {
            // Handle updating existing payment method (just the name and default status)
            try {
                setIsSubmitting(true);
                const error = await onSave({
                    cardHolder: cardHolderName,
                    expiryMonth: editingMethod.expiry_month,
                    expiryYear: editingMethod.expiry_year,
                    isDefault: isDefault
                });

                if (error) {
                    setErrors({ 
                        submit: error.response?.data?.message || 'Failed to update payment method' 
                    });
                    return;
                }
            } catch (error) {
                setErrors({ 
                    submit: error.response?.data?.message || 'Failed to update payment method' 
                });
            } finally {
                setIsSubmitting(false);
            }
            return;
        }

        // Handle adding new payment method
        if (!cardHolderName.trim()) {
            setErrors({ cardHolder: 'Card holder name is required' });
            return;
        }

        if (!clientSecret) {
            setErrors({ submit: 'Payment setup not initialized' });
            return;
        }

        setIsSubmitting(true);
        setErrors({});

        try {
            const cardElement = elements.getElement(CardElement);
            
            // First check if the setup intent is already succeeded
            const { setupIntent: existingSetupIntent } = await stripe.retrieveSetupIntent(clientSecret);
            
            let setupIntent;
            if (existingSetupIntent.status === 'succeeded') {
                // Setup intent already succeeded, use it directly
                setupIntent = existingSetupIntent;
            } else {
                // Confirm the setup intent
                const { error: stripeError, setupIntent: confirmedSetupIntent } = await stripe.confirmCardSetup(
                    clientSecret,
                    {
                        payment_method: {
                            card: cardElement,
                            billing_details: {
                                name: cardHolderName,
                            },
                        }
                    }
                );
                
                if (stripeError) {
                    setErrors({ 
                        submit: stripeError.message || 'Payment setup failed' 
                    });
                    return;
                }
                
                setupIntent = confirmedSetupIntent;
            }

            // Save the payment method to our backend
            const error = await onSave({
                paymentMethodId: setupIntent.payment_method,
                isDefault: isDefault
            });

            if (error) {
                setErrors({ 
                    submit: error.response?.data?.message || 'Failed to save payment method' 
                });
                return;
            }

            // Reset form
            setCardHolderName('');
            setIsDefault(false);
        } catch (error) {
            setErrors({ 
                submit: error.response?.data?.message || 'Failed to save payment method' 
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setIsSubmitting(false);
        setErrors({});
        setCardHolderName('');
        setIsDefault(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && handleCancel()}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>{editingMethod ? 'Edit Payment Method' : 'Add Payment Method'}</h2>
                    <button className={styles.closeButton} onClick={handleCancel}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGroup}>
                        <label>Card Holder Name</label>
                        <input
                            type="text"
                            placeholder="John Doe"
                            value={cardHolderName}
                            onChange={(e) => setCardHolderName(e.target.value)}
                            className={errors.cardHolder ? styles.error : ''}
                            disabled={isSubmitting}
                        />
                        {errors.cardHolder && <span className={styles.errorText}>{errors.cardHolder}</span>}
                    </div>

                    {!editingMethod && (
                        <div className={styles.formGroup}>
                            <label>Card Information</label>
                            <div className={styles.cardElementWrapper}>
                                <CardElement 
                                    options={cardElementOptions}
                                    className={styles.cardElement}
                                />
                            </div>
                            {errors.card && <span className={styles.errorText}>{errors.card}</span>}
                        </div>
                    )}

                    {editingMethod && (
                        <div className={styles.formGroup}>
                            <label>Current Card</label>
                            <div className={styles.existingCard}>
                                <i className={`fab fa-cc-${editingMethod.card_brand}`}></i>
                                •••• •••• •••• {editingMethod.last_four}
                                <span>Expires {editingMethod.expiry_month}/{editingMethod.expiry_year}</span>
                            </div>
                        </div>
                    )}

                    <div className={styles.formGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={isDefault}
                                onChange={(e) => setIsDefault(e.target.checked)}
                                disabled={isSubmitting}
                            />
                            Set as default payment method
                        </label>
                    </div>

                    {errors.setup && <div className={styles.errorText}>{errors.setup}</div>}
                    {errors.submit && <div className={styles.errorText}>{errors.submit}</div>}

                    <div className={styles.modalActions}>
                        <button
                            type="button"
                            className={styles.cancelButton}
                            onClick={handleCancel}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={styles.saveButton}
                            disabled={!stripe || isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    {editingMethod ? 'Updating...' : 'Adding...'}
                                </>
                            ) : (
                                editingMethod ? 'Update' : 'Add Payment Method'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default StripePaymentMethodModal; 